// Yoj recently viewed: the products a visitor has looked at, kept in their own browser, and the rows that show them.
// Product pages record each view (snippets/yoj-recently-viewed-record.liquid). A <yoj-recently-viewed> row starts
// empty and, once it nears the screen, asks the search page for its own section with exactly the products to show
// (q=id:1 OR id:2 ...); the section renders them as the theme's own product cards, in the order they were viewed.
// Nothing is kept until the visitor's cookie choice allows it (Theme settings > Yoj recently viewed), read from
// Shopify's Customer Privacy API ("preferences"); a later "no" forgets the list.

export const KEY = 'yoj-recently-viewed';
// The theme's own list (its search box shows four): read once to start ours, and emptied by Clear as well
export const THEME_KEY = 'viewedProducts';
export const LIMIT = 12;
export const CLEARED = 'yoj:recently-viewed-cleared';
const DAY = 24 * 60 * 60 * 1000;

const isId = (id) => /^\d{1,20}$/.test(id);

// The list, newest first, as [{ id, t }] where t is when it was viewed. Anything malformed, repeated or older than
// `days` is dropped, so a damaged entry can never break a page.
export function readList(storage, now, days) {
  let items;
  try {
    items = JSON.parse(storage.getItem(KEY) ?? 'null')?.items;
  } catch {
    return [];
  }
  if (!Array.isArray(items)) return [];
  const oldest = now - days * DAY;
  const seen = new Set();
  const list = [];
  for (const item of items) {
    const id = String(item?.id ?? '');
    if (!isId(id) || seen.has(id) || !Number.isFinite(item.t) || item.t < oldest) continue;
    seen.add(id);
    list.push({ id, t: item.t });
  }
  return list.slice(0, LIMIT);
}

// The theme's own list: product ids, newest first
export function readThemeList(storage) {
  try {
    const ids = JSON.parse(storage.getItem(THEME_KEY) ?? '[]');
    return Array.isArray(ids) ? [...new Set(ids.map(String).filter(isId))] : [];
  } catch {
    return [];
  }
}

// A view puts the product first. The first one ever starts from the theme's list, so a returning visitor's row isn't
// empty on day one.
export function recordView(storage, id, now, days) {
  const items = storage.getItem(KEY) === null
    ? readThemeList(storage).map((themeId) => ({ id: themeId, t: now }))
    : readList(storage, now, days);
  const list = [{ id, t: now }, ...items.filter((item) => item.id !== id)].slice(0, LIMIT);
  storage.setItem(KEY, JSON.stringify({ v: 1, items: list }));
  return list;
}

// What the visitor's cookie choice means for the list: 'keep' it, 'forget' it (they've turned preferences down), or
// 'wait' (no answer yet, or the privacy API can't say): nothing new is kept then, and nothing is thrown away, so a
// moment when the API isn't ready (or a clock that's wrong) never costs a visitor who accepted their list.
// "accepted": keep once they've accepted preferences (Shopify answers yes where it doesn't ask); "not_declined": keep
// unless they've said no; "always": keep regardless.
export function decide(mode, privacy) {
  if (mode === 'always') return 'keep';
  try {
    const answer = privacy?.currentVisitorConsent?.()?.preferences;
    if (answer === 'no') return 'forget';
    if (mode === 'not_declined') return 'keep';
    return privacy?.preferencesProcessingAllowed?.() ? 'keep' : 'wait';
  } catch {
    return mode === 'not_declined' ? 'keep' : 'wait';
  }
}

// The same, from the visitor's answer to the banner (Shopify's visitorConsentCollected event): an answer without
// preferences is a no
export function decideByChoice(mode, detail) {
  if (mode === 'always') return 'keep';
  return detail?.preferencesAllowed ? 'keep' : 'forget';
}

// Shopify's Customer Privacy API. The cookie banner has usually loaded it already; null if it can't load.
export function loadPrivacy(win = window, wait = 5000) {
  if (win.Shopify?.customerPrivacy) return Promise.resolve(win.Shopify.customerPrivacy);
  if (typeof win.Shopify?.loadFeatures !== 'function') return Promise.resolve(null);
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), wait);
    try {
      win.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        clearTimeout(timer);
        resolve(error ? null : win.Shopify.customerPrivacy ?? null);
      });
    } catch {
      clearTimeout(timer);
      resolve(null);
    }
  });
}

let privacy;
const getPrivacy = () => (privacy ??= loadPrivacy());

// Browsers can refuse storage (private windows, blocked site data): then nothing is kept or shown
function localStore() {
  try {
    const storage = window.localStorage;
    storage.getItem(KEY);
    return storage;
  } catch {
    return null;
  }
}

function forget(storage, theme = false) {
  try {
    storage?.removeItem(KEY);
    if (theme) storage?.removeItem(THEME_KEY);
  } catch {}
}

// Product pages: remember this product now if the visitor's choice allows it, or as soon as they allow it
export async function record(productId, { consent = 'accepted', days = 60 } = {}) {
  const id = String(productId);
  if (!isId(id)) return;
  const apply = (decided) => {
    const storage = localStore();
    if (decided === 'forget') return forget(storage);
    if (decided !== 'keep' || !storage) return;
    try {
      recordView(storage, id, Date.now(), Number(days) || 60);
    } catch {}
  };
  let answered = false;
  document.addEventListener('visitorConsentCollected', (event) => {
    answered = true;
    apply(decideByChoice(consent, event.detail));
  });
  const api = consent === 'always' ? null : await getPrivacy();
  if (!answered) apply(decide(consent, api));
  // Back on this page from the browser's memory (no reload): it's the latest view again
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) apply(decide(consent, api));
  });
}

const root = () => window.Shopify?.routes?.root ?? '/';
const searchUrl = () => window.Theme?.routes?.search_url ?? `${root()}search`;

function defineRow() {
  if (typeof customElements === 'undefined' || customElements.get('yoj-recently-viewed')) return;

  class YojRecentlyViewed extends HTMLElement {
    connectedCallback() {
      // A filled copy fetched from the search page is never connected itself: its contents are moved in
      if (this.started || 'filled' in this.dataset) return;
      this.started = true;
      if ('off' in this.dataset) return forget(localStore());
      if ('sample' in this.dataset) return this.markSample();
      this.start();
    }

    disconnectedCallback() {
      this.stopNear?.();
      document.removeEventListener('visitorConsentCollected', this.onConsent);
      document.removeEventListener(CLEARED, this.onCleared);
      document.removeEventListener('shopify:cart:lines-update', this.onCart);
      window.removeEventListener('pageshow', this.onPageShow);
    }

    // A footer row stands aside on a page that has a row of its own, such as the product page and the basket
    get standsAside() {
      return this.dataset.location === 'footer'
        && [...document.querySelectorAll('yoj-recently-viewed')].some((row) => row !== this && row.dataset.location !== 'footer');
    }

    // The theme editor shows sample products; say so, and say when visitors won't see this row on this page
    markSample() {
      const note = this.querySelector('[data-ref="sample-note"]');
      if (note && this.standsAside) note.textContent = note.dataset.asideText;
    }

    async start() {
      if (this.standsAside) return;
      const consent = this.dataset.consent ?? 'accepted';
      this.exclude = new Set((this.dataset.exclude ?? '').split(',').filter(isId));
      this.onConsent = (event) => {
        this.decided = decideByChoice(consent, event.detail);
        this.refresh();
      };
      this.onCleared = (event) => {
        if (event.detail?.row !== this) this.empty();
      };
      this.onPageShow = (event) => {
        if (event.persisted) this.refresh();
      };
      document.addEventListener('visitorConsentCollected', this.onConsent);
      document.addEventListener(CLEARED, this.onCleared);
      window.addEventListener('pageshow', this.onPageShow);
      if ('cart' in this.dataset) {
        this.onCart = (event) => this.cartChanged(event);
        document.addEventListener('shopify:cart:lines-update', this.onCart);
      }
      const api = consent === 'always' ? null : await getPrivacy();
      if (this.decided === undefined) this.decided = decide(consent, api);
      await this.near();
      this.refresh();
    }

    // Resolves once the row is within about a screen and a half of being seen, so pages that are never scrolled that
    // far never ask for it. Measured whenever anything scrolls: Dwell scrolls an inner container on desktop, where an
    // IntersectionObserver's margin doesn't reach (the row only loaded once it was already on screen).
    near() {
      return new Promise((resolve) => {
        let frame = 0;
        const check = () => {
          frame = 0;
          if (this.getBoundingClientRect().top > innerHeight + 1200) return;
          this.stopNear();
          resolve();
        };
        const later = () => {
          if (!frame) frame = requestAnimationFrame(check);
        };
        this.stopNear = () => {
          cancelAnimationFrame(frame);
          document.removeEventListener('scroll', later, true);
          window.removeEventListener('resize', later);
        };
        document.addEventListener('scroll', later, { capture: true, passive: true });
        window.addEventListener('resize', later, { passive: true });
        check();
      });
    }

    async refresh() {
      const ticket = (this.ticket = (this.ticket ?? 0) + 1);
      const storage = localStore();
      if (this.decided !== 'keep') {
        if (this.decided === 'forget') forget(storage);
        return this.empty();
      }
      const days = Number(this.dataset.days) || 60;
      const ids = storage ? readList(storage, Date.now(), days).map((item) => item.id).filter((id) => !this.exclude.has(id)) : [];
      const min = Math.max(1, Number(this.dataset.min) || 1);
      if (ids.length < min) return this.empty();
      const wanted = ids.join(',');
      if (wanted === this.shown) return;
      let fresh = null;
      try {
        const url = new URL(searchUrl(), location.origin);
        url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
        url.searchParams.set('type', 'product');
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url, { headers: { Accept: 'text/html' } });
        if (response.ok) {
          fresh = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('yoj-recently-viewed[data-filled]');
        }
      } catch {}
      // A newer refresh (a cookie answer, a basket change) has started meanwhile
      if (ticket !== this.ticket) return;
      // Products deleted, unpublished or sold out since are left out by the section, which may leave too few
      if (!fresh || Number(fresh.dataset.count) < min) return this.empty();
      this.replaceChildren(...[...fresh.childNodes].map((node) => document.importNode(node, true)));
      this.shown = wanted;
      this.querySelector('[data-ref="clear"]')?.addEventListener('click', () => this.clear());
    }

    empty() {
      this.replaceChildren();
      this.shown = '';
    }

    // Forgets the list (the theme's too, as its search box shows the same thing), empties every other row and
    // says so here, keeping the keyboard in the same place
    clear() {
      forget(localStore(), true);
      document.dispatchEvent(new CustomEvent(CLEARED, { detail: { row: this } }));
      this.shown = 'cleared';
      const body = this.querySelector('[data-ref="body"]');
      const note = this.querySelector('[data-ref="cleared"]');
      if (!body || !note) return this.empty();
      body.toggleAttribute('data-cleared', true);
      note.hidden = false;
      note.focus();
    }

    // The basket page leaves out what's in the basket, so a change there can change the row
    async cartChanged(event) {
      try {
        await event.promise;
      } catch {}
      try {
        const response = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = await response.json();
        this.exclude = new Set(cart.items.map((item) => String(item.product_id)));
        this.refresh();
      } catch {}
    }
  }

  customElements.define('yoj-recently-viewed', YojRecentlyViewed);
}

defineRow();
